import { createSelector } from 'reselect';
import { TEMP_REFS } from 'lib/constants';
import { getLatestActivitySet } from './getLatestActivitySet';
import { getIsLatestActivitySetOpen } from './getIsLatestActivitySetOpen';
import { getLatestActivityRef } from './getLatestActivityRef';
import { getPostLaborUpdatesOnNats } from './getPostLaborUpdatesOnNats';
import { getRawCurrentLaborTicket } from './getLaborTickets';

/**
 * The job start or activity change the server has confirmed on this machine, as
 * { workOrderOperationRef, startRef }, or null. Used to write material picks made
 * at job start only once the operator's chosen activity is running.
 *
 * Starting a job opens an optimistic activity set and activity (TEMP_REFS) before
 * the server answers, and deletes them if the start fails, so an open activity set
 * alone is not proof. Outside NATS mode the set and its latest activity must carry
 * real refs, and the activity ref is the startRef: a new start and a change of
 * activity both create an activity, but a change of activity keeps the activity
 * set. In NATS mode the activity is synthesized from the labor ticket the server
 * returned, so the labor ticket ref is the startRef instead.
 *
 * startRef identifies the start itself, so a caller can tell a new start of a WOO
 * from the one that was already running.
 */
const getConfirmedRunningStart = createSelector(
  [
    getLatestActivitySet,
    getIsLatestActivitySetOpen,
    getLatestActivityRef,
    getPostLaborUpdatesOnNats,
    getRawCurrentLaborTicket,
  ],
  (
    latestActivitySet,
    isOpen,
    latestActivityRef,
    natsFlowEnabled,
    currentLaborTicket
  ) => {
    if (!isOpen) return null;

    const workOrderOperationRef = latestActivitySet?.workOrderOperationRef;
    if (workOrderOperationRef == null) return null;

    if (natsFlowEnabled) {
      const laborTicketRef = currentLaborTicket?.laborTicketRef;
      if (laborTicketRef == null) return null;
      return { workOrderOperationRef, startRef: laborTicketRef };
    }

    const activitySetRef = latestActivitySet?.activitySetRef;
    if (
      activitySetRef == null ||
      activitySetRef === TEMP_REFS.ACTIVITY_SET_REF ||
      latestActivityRef == null ||
      latestActivityRef === TEMP_REFS.ACTIVITY_REF
    ) {
      return null;
    }
    return { workOrderOperationRef, startRef: latestActivityRef };
  }
);

export { getConfirmedRunningStart };
